import React, { useState } from 'react';
import Confetti from 'react-confetti';
import './FeedbackPopup.css';

const FeedbackPopup = ({ 
  isVisible, 
  onClose, 
  onSubmit, 
  onSkip,
  currentVoice 
}) => {
  const [responses, setResponses] = useState({
    easeOfUse: '',
    accuracy: '',
    journalSummaries: '',
    comparison: '',
    overallSatisfaction: '',
    suggestions: '',
  });
  
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);


  const questions = [
    {
      id: 'easeOfUse',
      title: 'Ease of Use',
      question: 'How easy was it for you to interact with Preptune and have it understand you?',
      type: 'rating'
    },
    {
      id: 'accuracy',
      title: 'Accuracy & Understanding',
      question: 'Did Preptune accurately remember important details from your past conversations?',
      type: 'rating'
    },
    {
      id: 'journalSummaries',
      title: 'Journal Summaries',
      question: 'How helpful were the journal summaries in reflecting on your thoughts or progress?',
      type: 'rating'
    },
    {
      id: 'comparison',
      title: 'Comparison',
      question: 'What do you like most about using Preptune compared to other similar tools (e.g., ChatGPT voice)?',
      type: 'text'
    },
    {
      id: 'overallSatisfaction',
      title: 'Overall Experience',
      question: 'Did you encounter any issues or glitches while using Preptune?',
      type: 'text'
    },
    {
      id: 'suggestions',
      title: 'Suggestions for Improvement',
      question: 'What features or changes would make Preptune even more useful for you?',
      type: 'text'
    }
  ];

  const handleRatingChange = (value) => {
    const questionId = questions[currentQuestion].id;
    setResponses(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleTextChange = (value) => {
    const questionId = questions[currentQuestion].id;
    setResponses(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    // Show confetti immediately when submission starts
    setShowConfetti(true);
    
    try {
      await onSubmit(responses);
      // Show thank you message with overlay
      setShowThankYou(true);
      
      // Hide thank you message after 2 seconds
      setTimeout(() => {
        setShowThankYou(false);
      }, 3000);
      
      // Keep confetti showing for 4 seconds to let pieces fall completely
      setTimeout(() => {
        setShowConfetti(false);
      },4500);
    } catch (error) {
      // Hide confetti immediately if submission fails
      setShowConfetti(false);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const isCurrentQuestionAnswered = () => {
    const questionId = questions[currentQuestion].id;
    const answer = responses[questionId];
    return answer && answer.toString().trim() !== '';
  };

  const isLastQuestion = currentQuestion === questions.length - 1;
  const currentQ = questions[currentQuestion];

  return (
    <>
      {/* Thank you message with overlay */}
      {showThankYou && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backdropFilter: 'blur(2px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            backgroundColor: '#f3f4f6',
            color: '#6b7280',
            fontSize: '14px',
            fontWeight: '500',
            padding: '12px 24px',
            borderRadius: '20px',
            textAlign: 'center',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            border: '1px solid #e5e7eb',
            animation: 'fadeInScale 0.6s ease-out'
          }}>
            Thank you for your feedback! 🎉
          </div>
        </div>
      )}
      
      {/* Confetti positioned outside the conditional render */}
      {showConfetti && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          pointerEvents: 'none',
          zIndex: 9999
        }}>
          <Confetti
            width={window.innerWidth}
            height={window.innerHeight}
            recycle={true}
            numberOfPieces={30}
            gravity={0.1}
            initialVelocityY={10}
            wind={0.002}             // optional: slight side drift for realism
            run={true}
          />
        </div>
      )}
      
      {!isVisible ? null : (
    <div className="feedback-overlay">
      
      <div className="feedback-popup">
        <div className="feedback-header">
          <div className="feedback-title">
            <div>
              <h3>How was your experience?</h3>
              <p>Your feedback helps us improve Preptune</p>
            </div>
          </div>
          
          <div className="feedback-progress">
            <span className="progress-text">{currentQuestion + 1} of {questions.length}</span>
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
              />
            </div>
          </div>
        </div>

        <div className="feedback-content">
          <div className="question-section">
            <h4 className="question-title">{currentQ.title}</h4>
            <p className="question-text">{currentQ.question}</p>
          </div>

          {currentQ.type === 'rating' && (
            <div className="rating-section">
              <div className="rating-labels">
                <span>Poor</span>
                <span>Excellent</span>
              </div>
              <div className="rating-buttons">
                {[1, 2, 3, 4, 5].map((rating) => (
                  <button
                    key={rating}
                    className={`rating-button ${responses[currentQ.id] === rating ? 'selected' : ''}`}
                    onClick={() => handleRatingChange(rating)}
                  >
                    {rating}
                  </button>
                ))}
              </div>
            </div>
          )}

          {currentQ.type === 'text' && (
            <div className="text-section">
              <textarea
                className="feedback-textarea"
                placeholder="Share your thoughts and suggestions..."
                value={responses[currentQ.id] || ''}
                onChange={(e) => handleTextChange(e.target.value)}
                rows={4}
              />
              <div className="text-help">
                Optional: Tell us what features or improvements would be most valuable to you
              </div>
            </div>
          )}
        </div>

        <div className="feedback-actions">
          <div className="action-left">
            {currentQuestion > 0 && (
              <button 
                className="feedback-btn secondary"
                onClick={handlePrevious}
              >
                Previous
              </button>
            )}
          </div>
          
          <div className="action-right">
            <button 
              className="feedback-btn skip"
              onClick={onSkip}
            >
              Skip for now
            </button>
            
            {!isLastQuestion ? (
              <button 
                className="feedback-btn primary"
                onClick={handleNext}
                disabled={!isCurrentQuestionAnswered()}
              >
                Next
              </button>
            ) : (
              <button 
                className="feedback-btn primary"
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <div className="loading-spinner"></div>
                    loading
                  </>
                ) : (
                  'Submit'
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
    )}
    </>
  );
};

export default FeedbackPopup;